import { Button } from '@components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@components/ui/popover';
import { SimpleTooltipV2 } from '@components/ui/tooltip';
import { useStore } from '@store/index';
import { Clock } from 'lucide-react';
import React, { useState } from 'react';

import { cn } from '@/utils';
import { formatPayerHoursToday } from '@/utils/payerHours';

import { type PayerHoursCase, usePayerHours } from '../hooks/usePayerHours';

export const PAYER_HOURS_UNAVAILABLE = 'Payer hours unavailable';

interface PayerHoursPopoverProps {
  caseItem: PayerHoursCase;
  /** Extra trigger classes, e.g. to match a neighbouring icon's shade (BV-2654). */
  triggerClassName?: string;
}

/**
 * Clock hover icon that opens the case's payer hours for today (BV-2525), in the timezone picked
 * in the header, formatted exactly like the Call tooltip's "Payer Hours" row (BV-2480). The hours
 * are fetched only when the popover opens, once per payer and workflow type. Where the Call
 * tooltip hides the row, the popover says "Payer hours unavailable": the icon is shown before
 * anything is known about the case, so an empty popover would read as broken.
 *
 * Clicks and keys inside never reach the card or row behind it, so opening the
 * popover does not open the case. The content renders in a portal, but React
 * events still bubble through the component tree to the card's handlers.
 *
 * The trigger also prevents the click's default, so inside a row `<Link>` (My Cases,
 * BV-2654) it does not follow the link, and it toggles `open` itself: Radix's trigger skips its
 * own toggle once the event is default-prevented.
 */
export function PayerHoursPopover({ caseItem, triggerClassName }: PayerHoursPopoverProps) {
  const [open, setOpen] = useState(false);
  const stop = (e: React.SyntheticEvent) => e.stopPropagation();

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <SimpleTooltipV2 content="Payer hours">
        <PopoverTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            aria-label="Payer hours"
            className={cn(
              'size-6 shrink-0 transition-opacity',
              'opacity-0 group-hover:opacity-100 focus-visible:opacity-100',
              'data-[state=open]:opacity-100',
              triggerClassName,
            )}
            onClick={e => {
              e.preventDefault();
              e.stopPropagation();
              setOpen(o => !o);
            }}
            onKeyDown={stop}
          >
            <Clock className="size-3.5" />
          </Button>
        </PopoverTrigger>
      </SimpleTooltipV2>
      <PopoverContent
        align="end"
        className="w-auto min-w-[180px] p-3 text-xs"
        onClick={stop}
        onKeyDown={stop}
        onWheel={stop}
      >
        <PayerHoursBody caseItem={caseItem} />
      </PopoverContent>
    </Popover>
  );
}

function PayerHoursBody({ caseItem }: { caseItem: PayerHoursCase }) {
  const timeZone = useStore(state => state.timeZone);
  const { data, isLoading } = usePayerHours(caseItem);

  if (isLoading) {
    return <div className="text-muted-foreground">Loading payer hours…</div>;
  }
  const label = formatPayerHoursToday(data?.payer_hours, timeZone);
  if (!label) {
    return <div className="text-muted-foreground">{PAYER_HOURS_UNAVAILABLE}</div>;
  }
  return (
    <div className="flex flex-col gap-1">
      <span className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
        Payer Hours
      </span>
      <span className="font-medium text-foreground whitespace-nowrap">{label}</span>
    </div>
  );
}
